import { Ceil } from "@pdfking/math"

import type { GrowLayout } from "#public"
import {
  Across,
  Along,
  Content,
  DIM_HEIGHT,
  DIM_WIDTH,
  NO_PAGES,
  PAGE_ITSELF,
  Pages,
  Pair,
  RecordByPrimaryKey,
  makeGrowEntry,
  ContentRenderer,
  GrowContentKind,
} from "#public"

// A page of the signature: its front and rear in the reading order, a
// left-hand page mirrored along the opening.
const page = (front: number, rear: number, rotated: boolean): GrowLayout =>
  Content(
    GrowContentKind.PAGE,
    DIM_WIDTH,
    Pages(front, rear, {
      rotation: rotated ? 180 : 0,
      flip: front % 2 === 0 ? "along" : undefined,
    })
  )

const P = (front: number, rear: number): GrowLayout => page(front, rear, false)

const R = (front: number, rear: number): GrowLayout => page(front, rear, true)

// A blank of the signature: a page with no place in the order, named nothing.
const E: GrowLayout = Content(GrowContentKind.PAGE, DIM_WIDTH, { ...NO_PAGES, label: "" })

// Rows of pages from the top down, each row's pages left to right in pairs
// folded together.
const signature = (rows: GrowLayout[][]): GrowLayout =>
  Across(
    rows.map(row =>
      Along(
        Array.from({ length: Ceil(row.length / 2) }, (_, i) => Pair(row.slice(2 * i, 2 * i + 2))),
        DIM_HEIGHT
      )
    )
  )

const book = <I extends string>(id: I, name: string, rows: GrowLayout[][], sides_tuple: number[]) =>
  makeGrowEntry(
    id,
    name,
    "SEGNATURA",
    signature(rows),
    {
      is_book: true,
      is_single_page: false,
      show_crossmark_on_fold: false,
      renderer: ContentRenderer.BOOK,
    },
    { sides_tuple }
  )

export const BookGrowList = RecordByPrimaryKey([
  // A loose page of the book, the page as it is: no signature (`is_book`
  // false), drawn as the book's, page 1 its cover; the product's pages come as
  // these.
  makeGrowEntry("bp", "LOOSE_PAGE", "SEGNATURA", PAGE_ITSELF, {
    is_single_page: true,
    renderer: ContentRenderer.BOOK,
  }),

  book("b04", "GROW_KIND_B04", [[P(4, 3), P(1, 2)]], [4]),

  book(
    "b08",
    "GROW_KIND_B08",
    [
      [R(5, 6), R(4, 3)],
      [P(8, 7), P(1, 2)],
    ],
    [8]
  ),

  book("b08_alt", "GROW_KIND_B08_ALT", [[P(4, 3), P(5, 6), P(8, 7), P(1, 2)]], [8]),

  book(
    "b20_alt",
    "GROW_KIND_B20_ALT",
    [
      [R(5, 6), R(4, 3)],
      [P(8, 7), P(1, 2)],
      [R(13, 14), R(12, 11)],
      [P(16, 15), P(9, 10)],
      [P(20, 19), P(17, 18)],
    ],
    [8, 8, 4]
  ),

  book(
    "b12",
    "GROW_KIND_B12",
    [
      [P(8, 7), P(5, 6)],
      [R(9, 10), R(4, 3)],
      [P(12, 11), P(1, 2)],
    ],
    [12]
  ),

  book(
    "b16",
    "GROW_KIND_B16",
    [
      [R(5, 6), R(12, 11), R(9, 10), R(8, 7)],
      [P(4, 3), P(13, 14), P(16, 15), P(1, 2)],
    ],
    [16]
  ),

  book(
    "b16_alt",
    "GROW_KIND_B16_ALT",
    [
      [R(9, 10), R(8, 7)],
      [P(12, 11), P(5, 6)],
      [R(13, 14), R(4, 3)],
      [P(16, 15), P(1, 2)],
    ],
    [16]
  ),

  book(
    "b24",
    "GROW_KIND_B24",
    [
      [P(8, 7), P(17, 18), P(20, 19), P(5, 6)],
      [R(9, 10), R(16, 15), R(21, 22), R(4, 3)],
      [P(12, 11), P(13, 14), P(24, 23), P(1, 2)],
    ],
    [24]
  ),

  book(
    "b28",
    "GROW_KIND_B28",
    [
      [R(5, 6), R(12, 11), R(9, 10), R(8, 7)],
      [P(4, 3), P(13, 14), P(16, 15), P(1, 2)],
      [R(21, 22), R(20, 19), E, E],
      [P(24, 23), P(17, 18), P(28, 27), P(25, 26)],
    ],
    [16, 8, 4]
  ),

  book(
    "b32",
    "GROW_KIND_B32",
    [
      [R(9, 10), R(24, 23), R(17, 18), R(16, 15)],
      [P(8, 7), P(25, 26), P(32, 31), P(1, 2)],
      [R(5, 6), R(28, 27), R(29, 30), R(4, 3)],
      [P(12, 11), P(21, 22), P(20, 19), P(13, 14)],
    ],
    [32]
  ),
] as const)
